import { useMemo } from 'react'
import { Pin, PinOff } from 'lucide-react'

import type { FleetVehicle } from '@/lib/api/fleet-api'
import { FleetCameraTile } from '@/components/tracking/fleet-camera-tile'
import { useVehicleCameraLatest } from '@/hooks/use-vehicle-gps-query'
import {
    addressText,
    lastUpdateText,
    speedText,
    todayDistanceText,
} from '@/lib/fleet-format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    vehicle: FleetVehicle
    pinned: boolean
    onTogglePin: (id: string) => void
}

// Xe được spotlight: mọi kênh camera live (autoStart) theo grid thích ứng +
// khối định vị. Gom photos theo camera_id như fleet-camera-overlay; placeholder
// kênh 1 nếu has_camera nhưng chưa có ảnh. Không có camera → null.
export function StationSpotlightCard({ vehicle, pinned, onTogglePin }: Props) {
    const hasCamera = Boolean(vehicle.gps?.has_camera)
    const latest = useVehicleCameraLatest(vehicle.vehicle_id, hasCamera)

    const cams = useMemo(() => {
        const photos = latest.data?.photos ?? []
        const byId = new Map<
            number,
            { id: number; name: string; url?: string }
        >()
        for (const p of photos) {
            if (!byId.has(p.camera_id)) {
                byId.set(p.camera_id, {
                    id: p.camera_id,
                    name: p.camera_name,
                    url: p.image_url,
                })
            }
        }
        if (byId.size === 0) {
            // VnetGPS chỉ có cờ has_camera, không có số kênh. Xe có camera mà
            // chưa upload ảnh → 1 placeholder kênh 1 để vẫn bật được live.
            return hasCamera
                ? [{ id: 1, name: m.vehicle_camera_channel({ id: 1 }) }]
                : null
        }
        // Sort theo id cho thứ tự kênh ổn định giữa các lần poll.
        return Array.from(byId.values()).sort((a, b) => a.id - b.id)
    }, [latest.data, hasCamera])

    const { gps } = vehicle

    return (
        <div className="station-spot" data-testid="station-spotlight">
            <div className="station-spot-head">
                <span className="station-spot-plate">
                    {vehicle.plate_number}
                </span>
                <span className="station-spot-speed">{speedText(vehicle)}</span>
                <button
                    type="button"
                    className={`station-spot-pin ${pinned ? 'is-pinned' : ''}`}
                    aria-pressed={pinned}
                    onClick={() => onTogglePin(vehicle.vehicle_id)}
                    title={
                        pinned
                            ? m.station_fleet_pinned()
                            : m.station_fleet_rotating()
                    }
                >
                    {pinned ? (
                        <Pin className="size-4" />
                    ) : (
                        <PinOff className="size-4" />
                    )}
                </button>
            </div>

            {cams ? (
                <div className="station-spot-cams" data-cams={cams.length}>
                    {cams.map((c) => (
                        <FleetCameraTile
                            key={c.id}
                            fill
                            autoStart
                            vehicleId={vehicle.vehicle_id}
                            cameraId={c.id}
                            cameraName={c.name}
                            imageUrl={c.url}
                        />
                    ))}
                </div>
            ) : (
                <div className="station-spot-nocam">{m.fleet_no_camera()}</div>
            )}

            <p className="station-spot-addr">{addressText(vehicle)}</p>
            {gps && (
                <dl className="station-spot-stats">
                    <SpotStat
                        label={m.fleet_coordinates()}
                        value={`${gps.latitude.toFixed(5)}, ${gps.longitude.toFixed(5)}`}
                    />
                    <SpotStat
                        label={m.fleet_direction()}
                        value={`${gps.direction}°`}
                    />
                    <SpotStat
                        label={m.tracking_today_distance()}
                        value={todayDistanceText(vehicle)}
                    />
                    <SpotStat
                        label={m.fleet_stat_updated()}
                        value={lastUpdateText(vehicle)}
                    />
                </dl>
            )}
        </div>
    )
}

function SpotStat({ label, value }: { label: string; value: string }) {
    return (
        <div className="station-spot-stat">
            <dt>{label}</dt>
            <dd>{value}</dd>
        </div>
    )
}
